/**
 * @fileoverview The Discover v2 row meta line (WEB-788 / DPL-19 / PAR-28).
 *
 * One mono line per row carrying the row's *facts*:
 *
 * ```
 * VENUE · capacity
 * ```
 *
 * WEB-1228 (ruling R2): distance and walk time are gone from the row. The
 * `/discover` query is centered on the city anchor, not on the reader, so a
 * per-row mileage or walking-time token claimed a starting point the reader
 * never set.
 *
 * The neighborhood is deliberately NOT a token. WEB-781/787 put the hood
 * label in the spine column beside its footprint glyph at every width, so
 * repeating it here would print the same fact twice on one row.
 *
 * Every token is honest-absence: a fact we do not hold is omitted, never
 * fabricated and never left as a dangling separator.
 *
 * `formatDistanceMilesLabel` stays here for the event-detail Getting There
 * copy (`lib/gettingThere/walkDistanceCopy.ts`).
 *
 * @since WEB-788
 */

import { isResolvedVenue, type DiscoverVenueKind } from '@loci/types';

import { formatCount } from '@/lib/discover/formatCount';

/**
 * Metres per statute mile. Matches the backend's `METERS_PER_MILE` in
 * `services/backend-api/src/services/discover/mapWhyReasonToDisplay.ts`.
 */
export const METERS_PER_MILE = 1609.344;

/**
 * `0.7 mi` / `3 mi`.
 *
 * Rounding mirrors the backend proximity copy exactly: one decimal under a
 * mile (`0.4 mi` reads naturally), whole miles above. Returns `null` when the
 * distance is absent, non-finite, negative, **or rounds to zero**: a 30 m
 * venue is not "0 mi".
 */
export function formatDistanceMilesLabel(distanceMeters: number | undefined): string | null {
  if (distanceMeters === undefined || !Number.isFinite(distanceMeters) || distanceMeters < 0) {
    return null;
  }
  const miles = distanceMeters / METERS_PER_MILE;
  const rounded = miles < 1 ? Math.round(miles * 10) / 10 : Math.round(miles);
  if (rounded <= 0) return null;
  return `${rounded} mi`;
}

/**
 * Format a nominal venue capacity as `N cap` with thousands separators
 * (`2,000 cap`). WEB-1189 C15: the meta token stays mono but not all caps.
 * Returns `null` for an absent / non-finite / non-positive capacity so the
 * line omits the affordance rather than printing a fabricated `0 cap`.
 *
 * The positivity test is applied to the **rounded** figure, not the raw one:
 * a fractional `0.4` clears `> 0` but rounds to `0`, and `0 CAP` is exactly
 * the fabrication this helper exists to refuse.
 */
export function formatCapacityLabel(capacity: number | undefined): string | null {
  if (capacity === undefined || !Number.isFinite(capacity) || capacity <= 0) {
    return null;
  }
  const rounded = Math.round(capacity);
  if (rounded <= 0) return null;
  return `${formatCount(rounded)} cap`;
}

/**
 * The subset of `DiscoverEvent` this module needs. Declared locally so the
 * pure token logic stays decoupled from the full wire contract.
 */
export interface DiscoverRowMetaInput {
  readonly venueName: string;
  readonly venueKind?: DiscoverVenueKind;
  /** Nominal venue capacity — authenticated-only on the wire. */
  readonly capacity?: number;
}

/**
 * Ordered meta-line tokens for one Discover row: venue, capacity. Empty when the row holds none of those facts (the caller then
 * renders no line at all rather than an empty paragraph).
 *
 * An unresolved `venueKind` (PIP-87 failed-normalisation address or brand)
 * drops the venue token — the raw fallback string is junk, and printing it
 * would be the same fabrication `formatDiscoverRowPlaceLine` already refuses.
 * The capacity token still renders, so a row never loses it to a bad venue
 * label.
 */
export function discoverRowMetaTokens(event: DiscoverRowMetaInput): readonly string[] {
  const venue = isResolvedVenue(event) ? event.venueName.trim() : '';
  const tokens: string[] = [];
  if (venue.length > 0) tokens.push(venue);

  const capacity = formatCapacityLabel(event.capacity);
  if (capacity !== null) tokens.push(capacity);

  return tokens;
}
